import { centsToEuros } from '@agos/shared'

import type { CartLine } from '@agos/customer-core'
import { scheduledAtOf } from './preorder'

/**
 * The body of `POST /api/v1/orders`, assembled from what the checkout knows.
 *
 * **What is deliberately not sent.**
 *
 * `discount` is omitted. `OrderCreationService` recomputes every discount for a web order and only reads
 * this field on the POS branch — *"a POS order carries whatever the till computed: the person typing it
 * is trusted."* Sending a figure the server discards makes the request look like it decides something it
 * does not, and the next person to read it has to check.
 *
 * `promo` is omitted because the field does not exist. Legacy sends
 * `promo: { blackFridayAmount, freeDelivery }` with a comment claiming the server re-validates it; the
 * AGOS request record has no such member, and `OrderCampaigns.blackFriday` works the window out from the
 * subtotal on its own.
 *
 * **`deliveryCost` is now checked server-side, and this paragraph used to say the opposite.**
 * `BACKEND.md` §10A.132: the fee is recomputed from the zone the order's coordinates fall in and
 * **raised** where the request understates it - equal keeps, higher keeps. That is not a licence to
 * send anything: what the server raises it to is the figure this app displayed, so `delivery.ts` still
 * reproduces the zone arithmetic exactly, and a disagreement means the customer is charged something
 * they were not shown. 96.7% of the owner's 4 551 geocoded web orders already agree to the cent.
 *
 * **`employeeId` and `manualDiscountType/Value/Operator` are not sent, and would be ignored if they
 * were** - §10A.133. They are a till's fields: one puts a sale in an employee's wallet, the others
 * label a discount as staff-granted in the owner's own report.
 *
 * **What must be sent, and why.**
 *
 * `X-Idempotency-Key` — a repeat carrying the same key answers the original order rather than placing a
 * second one. Generated once per checkout attempt, not per submit, or a double tap makes two orders.
 *
 * `Authorization` — a signed-in customer's token, so `ownsTheEmail` passes and their personal discount
 * is applied. Without it the server treats the email as unproven and declines the rate.
 *
 * `deliveryLat` / `deliveryLon` — the only thing `requireMinimumOrder` has to work with. Omitting them
 * skips the zone minimum entirely, which is the same hole FB-C10 opens by accident.
 */

export interface CheckoutFacts {
  /** The channel's slug — any the franchise offers on the web, not a fixed pair (§9.50). */
  type: string
  customerName: string
  customerPhone: string
  customerEmail: string
  customerAddress: string
  point: { lat: number; lng: number } | null
  paymentMethod: string
  notes: string
  deliveryCents: number
  couponCode: string | null
  referralCode: string | null
  redeemedPoints: number
  preorder: { date: string; time: string } | null
}

export interface OrderLinePayload {
  productId: number
  /** §9.85 — the size's label, `"50 cm"`. Absent for a product sold at one price. */
  size?: string
  quantity: number
  unitPrice: number
  customName?: string
  giftRuleId?: number
  giftRuleType?: string
  /**
   * What a composed line was assembled from — `BACKEND.md §10A.14`.
   *
   * Only ever set on a composed line, and only when the builder knew its parts. A line without it
   * prices exactly as it did before, so sending them is additive: what it buys is the cap
   * `ServerPricing` applies, which is `BLOCKED.md` #12's other half.
   */
  parts?: { productId?: number; name: string; unitPrice: number; quantity: number }[]
  /**
   * Which rule bounds this composed line — `BACKEND.md` §10A.26.
   *
   * `'kombo'` means the dearest part rather than the sum of them, because a kombo is priced `max` by
   * design. Absent on everything else, and a line without it is bounded exactly as it was before.
   */
  composition?: 'kombo'
  /**
   * Extra toppings on a catalogue pizza — `BACKEND.md` §10A.80.
   *
   * Which ingredient and how many portions, never what it costs: the server prices each from
   * `pizza_ingredients` at this line's size. `unitPrice` above carries the surcharge for the
   * screen's sake and the server has always discarded it, which is why this field exists.
   */
  extras?: { ingredientId: number; quantity: number }[]
}

export interface PricedCartLine {
  line: CartLine
  name: string
  unitCents: number
}

/** Blank strings are omitted rather than sent — an empty `customerEmail` fails `@Email`. */
function orUndefined(value: string): string | undefined {
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

/**
 * A basket line as the server wants it.
 *
 * A gift carries `productId: 0` and its rule, which is what `GiftRules.claimsAGift` looks for. The server
 * then checks the rule really was earned and drops the line if it was not, so this is a claim rather than
 * an instruction — which is the right way round.
 */
export function lineOf(priced: PricedCartLine): OrderLinePayload {
  const { line } = priced
  const gift = line.giftRuleId !== undefined && line.giftRuleId !== null
  return {
    productId: gift ? 0 : line.productId,
    quantity: line.quantity,
    // §10A.109 - a gift's price is no longer forced to zero here. It was, and the server forced it to
    // zero again, so the two agreed and a 2,00 € gift pizza was given away. The server now prices a
    // gift from `product_gift_rules.gift_price` and ignores this figure either way; sending the
    // basket's own number means the request says what the customer was shown.
    unitPrice: centsToEuros(priced.unitCents),
    customName:
      line.extras.trim() === '' ? undefined : `${priced.name} [${line.extras.trim()}]`,
    giftRuleId: gift ? line.giftRuleId : undefined,
    giftRuleType: gift ? 'product' : undefined,
    // §9.85 — which size, so `ServerPricing` prices it from the product's own `pizza_sizes` rather
    // than from its base price (`BACKEND.md` §10A.33). Omitted for a product sold at one price,
    // which is every line this payload carried before.
    size: line.size,
    // §10A.14 — the parts, in euros as every other price on this payload is.
    parts:
      line.composed?.parts === undefined
        ? undefined
        : line.composed.parts.map((part) => ({
            ...(part.productId === undefined ? {} : { productId: part.productId }),
            name: part.name,
            unitPrice: centsToEuros(part.unitPriceCents),
            quantity: part.quantity,
          })),
    // §10A.26 — which rule bounds this line. Absent for everything but a kombo.
    composition: line.composed?.composition,
    // §10A.80 — which toppings, never what they cost. `unitPrice` above is the catalogue price the
    // server re-reads anyway; the surcharge is charged from these ids, at this line's size. The
    // text stays in `customName` above, which is what the kitchen ticket reads.
    extras:
      line.extrasChosen === undefined || line.extrasChosen.length === 0
        ? undefined
        : line.extrasChosen.map((each) => ({ ...each })),
  }
}

/**
 * A line the catalogue cannot price, as the server takes it.
 *
 * `productId` is negative — the convention the till, the kitchen board and `OrderCreationController`
 * already share for a line assembled outside the catalogue — and the price travels with the line rather
 * than being looked up: `/products` carries neither the day's meals nor a kombo somebody just built. A
 * partly-bundled soup arrives here as two lines, which is why this takes them already split.
 */
export function lunchLineOf(line: {
  productId: number
  quantity: number
  unitPriceCents: number
  customName: string
  /** Set on a line the customer is being given free under a gift rule. */
  giftRuleId?: number
  /** §9.39 — the catalogue product a linked daily-menu meal deducts stock against. */
  parts?: { productId: number; name: string; unitPriceCents: number; quantity: number }[]
}): OrderLinePayload {
  const gift = line.giftRuleId !== undefined
  return {
    // `GiftRules.claimsAGift` looks for `productId: 0` and a rule id; without both the server prices the
    // line as an ordinary product rather than from the gift rule.
    productId: gift ? 0 : line.productId,
    quantity: line.quantity,
    // §10A.109 - not zeroed for a gift. The server decides a gift's price from
    // `product_gift_rules.gift_price`; zeroing it here only made the request disagree with the basket.
    unitPrice: centsToEuros(line.unitPriceCents),
    customName: line.customName,
    giftRuleId: line.giftRuleId,
    giftRuleType: gift ? 'product' : undefined,
    // §9.39. Not sent for a gift: a free line deducts stock the way the gift rule decides, and a
    // part priced at what the meal costs would contradict the zero above it.
    ...(!gift && line.parts && line.parts.length > 0
      ? {
          parts: line.parts.map((part) => ({
            productId: part.productId,
            name: part.name,
            unitPrice: centsToEuros(part.unitPriceCents),
            quantity: part.quantity,
          })),
        }
      : {}),
  }
}

export function orderPayload(
  facts: CheckoutFacts,
  lines: readonly PricedCartLine[],
  /**
   * The lines no catalogue can price: the daily menu, already split by the soup bundle rule, and
   * anything the customer assembled. Empty for a basket of ordinary products.
   */
  lunchLines: ReadonlyArray<{
    productId: number
    quantity: number
    unitPriceCents: number
    customName: string
    giftRuleId?: number
    /** §9.39 — the product a linked meal deducts stock against, priced by the menu. */
    parts?: { productId: number; name: string; unitPriceCents: number; quantity: number }[]
  }> = [],
): Record<string, unknown> {
  const delivery = facts.type === 'delivery'
  const scheduledAt =
    facts.preorder === null ? null : scheduledAtOf(facts.preorder.date, facts.preorder.time)

  return {
    type: facts.type,
    customerName: facts.customerName.trim(),
    customerPhone: facts.customerPhone.trim(),
    customerEmail: orUndefined(facts.customerEmail),
    customerAddress: delivery ? orUndefined(facts.customerAddress) : undefined,
    deliveryLat: delivery ? (facts.point?.lat ?? undefined) : undefined,
    deliveryLon: delivery ? (facts.point?.lng ?? undefined) : undefined,
    paymentMethod: facts.paymentMethod,
    notes: orUndefined(facts.notes),
    // Euros, because the wire is euros — cents are this app's internal unit, not the API's.
    deliveryCost: centsToEuros(delivery ? facts.deliveryCents : 0),
    couponCode: facts.couponCode ?? undefined,
    referralCode: facts.referralCode ?? undefined,
    redeemedPoints: facts.redeemedPoints > 0 ? facts.redeemedPoints : undefined,
    isPreorder: scheduledAt === null ? undefined : true,
    scheduledAt: scheduledAt ?? undefined,
    items: [...lines.map(lineOf), ...lunchLines.map(lunchLineOf)],
  }
}

/**
 * What must be true before the button does anything.
 *
 * The server's own constraints, said first: `@NotBlank type`, `@NotBlank paymentMethod`,
 * `@NotEmpty items`, `@Positive quantity`. A refusal the customer could have been told about before
 * filling the form is a refusal they experience as the site being broken.
 */
export function submitProblems(
  facts: CheckoutFacts,
  lines: readonly PricedCartLine[],
): string[] {
  const problems: string[] = []
  if (lines.length === 0) problems.push('empty-basket')
  if (facts.customerName.trim().length < 2) problems.push('name')
  if (facts.customerPhone.trim().length < 9) problems.push('phone')
  if (facts.paymentMethod.trim() === '') problems.push('payment-method')
  if (facts.type === 'delivery' && facts.customerAddress.trim().length < 5) problems.push('address')
  if (facts.preorder !== null && scheduledAtOf(facts.preorder.date, facts.preorder.time) === null) {
    problems.push('scheduled-at')
  }
  return problems
}
